import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  FilterField,
  FilterPanel,
  ConfirmDialog,
  LoadingState,
  Page,
  Select,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
} from "@marekl/adminer/components";
import { notifyError, notifySuccess, notifyWarning } from "@marekl/adminer/api";
import { useConfirm, useUrlFilters } from "@marekl/adminer/hooks";
import dayjs from "dayjs";
import { formatDateTime, formatTime } from "@marekl/adminer/format";
import { CHECKIN_PROBLEM_LABELS } from "../../../lib/checkInState";
import { AppTopbar } from "../../../shell/AppTopbar";
import { competitionCrumbs, useCompetitionReadOnly } from "../../../shell/CompetitionLayout";
import { useAuth } from "../../../auth/useAuth";
import { canFileEntryForAnyTeam, canReadAllMembers } from "../../../auth/queries";
import { isFrozen } from "../../../lib/competitionState";
import { useCompetition } from "../../../lib/queries/competitions";
import { useRuleSetDocument } from "../../../lib/queries/rules";
import {
  useCompetitionEntries,
  useDeleteEntry,
  useReinstateEntry,
  useWithdrawEntry,
  type Entry,
  type EntryCheckIn,
} from "../../../lib/queries/entries";
import { indexDocument, comboLabel } from "../../../lib/rulesDocument";
import styles from "./CompetitionEntriesPage.module.css";
import { EntryDetailModal } from "../../../components/EntryDetailModal";
import { TeamFlag } from "../../../components/TeamFlag";
import type { ID } from "../../../lib/id";

type Tab = "submitted" | "withdrawn";

type ModalState =
  | { kind: "closed" }
  | { kind: "withdraw"; entry: Entry }
  // An id rather than the row, so an upload made from inside the dialog
  // is reflected in it once the list refetches.
  | { kind: "detail"; id: ID };

// The organizer's Přihlášky tab: every entry of the competition grouped
// by combination, with filters, odhlášky and manual additions (for
// global managers, who may read rosters).
export function CompetitionEntriesPage() {
  const { id } = useParams();
  // A competitions_viewer reads this page; nothing on it is offered.
  const readOnly = useCompetitionReadOnly();
  const compId = id ?? "";
  const { user } = useAuth();
  // Reading another club's detail page is for the registry's readers only;
  // a competition-scoped organizer would just collect 403s, and a link that
  // 403s is worse than no link. Filing a přihláška for a club needs more
  // than reading it (canFileEntryForAnyTeam).
  const canReadTeams = canReadAllMembers(user);
  const canManuallyAdd = canFileEntryForAnyTeam(user);

  const { data: competition, isLoading, error, refetch } = useCompetition(compId);
  const { data: document } = useRuleSetDocument(competition?.rule_set_id);
  const { data: entries = [], isLoading: entriesLoading } = useCompetitionEntries(compId);
  // In Probíhá the organizer's verbs are live commands (R25).
  const live = competition?.status === "running" ? compId : undefined;
  const withdraw = useWithdrawEntry(live);
  const reinstate = useReinstateEntry(live);
  const remove = useDeleteEntry();

  const [tab, setTab] = useState<Tab>("submitted");
  // In the URL: an organizer who narrows the list to one club is usually
  // about to send somebody the link.
  const [searchParams, setSearchParams] = useSearchParams();
  const { setFilter } = useUrlFilters({
    searchParams,
    setSearchParams,
    paginationKey: "page",
  });
  const disciplineFilter = searchParams.get("discipline") ?? "";
  const teamFilter = searchParams.get("team") ?? "";
  // „Předáno z prezence" (A.13): only what the prezence left to resolve.
  const checkInFilter = searchParams.get("prezence") === "1";
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const confirm = useConfirm();
  const [withdrawNote, setWithdrawNote] = useState("");

  const index = useMemo(() => (document ? indexDocument(document) : null), [document]);

  const crumbs = competitionCrumbs(compId, competition, { label: "Přihlášky" });

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (error || !competition) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState title="Soutěž se nepodařilo načíst" error={error} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  const submitted = entries.filter((e) => e.status === "submitted");
  const withdrawn = entries.filter((e) => e.status === "withdrawn");

  const teamNames = [...new Set(entries.map((e) => e.team_name ?? ""))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, "cs"));

  const disciplineOf = (e: Entry) =>
    index?.combos.get(e.discipline_category_id)?.discipline;
  const matchesFilters = (e: Entry) =>
    (!disciplineFilter || disciplineOf(e)?.id === disciplineFilter) &&
    (!teamFilter || e.team_name === teamFilter) &&
    (!checkInFilter || !!e.checkin);

  const visible = (tab === "submitted" ? submitted : withdrawn).filter(matchesFilters);

  // Group the submitted view by combination — the organizer thinks in
  // startlist categories, not a flat list.
  const groups = new Map<ID, Entry[]>();
  for (const e of visible) {
    groups.set(e.discipline_category_id, [...(groups.get(e.discipline_category_id) ?? []), e]);
  }

  const close = () => {
    setModal({ kind: "closed" });
    setWithdrawNote("");
    withdraw.reset();
    remove.reset();
  };

  const detailEntry =
    modal.kind === "detail" ? (entries.find((e) => e.id === modal.id) ?? null) : null;

  const entryRows = (rows: Entry[]) => (
    <Table<Entry>
      rows={rows}
      rowKey={(e) => e.id}
      columns={[
        {
          key: "cp",
          header: "ČP",
          numeric: true,
          tdClass: styles.colCp,
          render: (e) => e.entry_number,
        },
        {
          key: "team",
          header: "Tým",
          tdClass: styles.colTeam,
          // undefined keeps the cell plain text, which is exactly what an
          // organizer without a global role needs: a link that 403s is worse
          // than no link.
          linkTo: (e) => (canReadTeams ? `/teams/${e.team_id}` : undefined),
          render: (e) => (
            <>
              {e.team_name ?? "—"}
              {e.team_country && <TeamFlag code={e.team_country} />}
            </>
          ),
        },
        {
          key: "competitors",
          header: "Soutěžící",
          render: (e) => (
            <>
              {e.competitors.map((c) => (
                <div key={c.team_membership_id}>
                  {c.person.first_name} {c.person.last_name}
                  {c.kind === "guest" && <Text variant="muted"> (nečlen)</Text>}
                </div>
              ))}
              {e.checkin && <CheckInNote checkin={e.checkin} />}
            </>
          ),
        },
        {
          key: "created",
          header: "Podáno",
          tdClass: styles.colCreated,
          render: (e) => (e.created_at ? formatDateTime(e.created_at) : "—"),
        },
        ...(tab === "withdrawn"
          ? [
              {
                key: "withdrawn",
                header: "Odhlášeno",
                tdClass: styles.colWithdrawn,
                render: (e: Entry) => (
                  <>
                    {e.withdrawn_at ? formatDateTime(e.withdrawn_at) : "—"}
                    {e.withdrawn_note && (
                      <Text variant="muted"> {e.withdrawn_note}</Text>
                    )}
                  </>
                ),
              },
            ]
          : []),
        {
          key: "actions",
          header: "",
          tdClass: styles.colActions,
          // An organizer may only withdraw — removing an entry has to
          // leave the trace the spec demands. Purging a withdrawn one
          // afterwards is the admin's tidy-up hatch.
          render: (e) => {
            // „Detail" is unconditional — it reads, it does not change
            // anything, so neither a published running order nor an
            // odhláška is a reason to withhold it.
            const detail = (
              <Table.Action appearance="button" onClick={() => setModal({ kind: "detail", id: e.id })}>
                Detail
              </Table.Action>
            );
            if (e.status === "submitted") {
              // What this user may do comes from the server
              // (e.permissions, entry.Compute): the organizer keeps every
              // change after the start list is published (R16), and has
              // nothing at all on a Dokončeno or archived competition.
              return (
                <Table.Actions>
                  {detail}
                  {e.permissions.can_withdraw && (
                    <Table.Action appearance="button" onClick={() => setModal({ kind: "withdraw", entry: e })}>
                      Odhlásit
                    </Table.Action>
                  )}
                </Table.Actions>
              );
            }
            return (
              <Table.Actions>
                {detail}
                {e.permissions.can_reinstate && (
                  <Table.Action
                    appearance="button"
                    onClick={() =>
                      reinstate.mutate(e.id, {
                        // Capacity does not refuse a reinstate, so this
                        // is the only place the organizer finds out they
                        // just went over it: the operation succeeded and
                        // there is still something to act on, which is
                        // exactly what notifyWarning is for.
                        onSuccess: (row) => {
                          return row?.over_capacity
                            ? notifyWarning(
                                `Přihláška je vrácena zpět. Soutěž je teď nad kapacitou ` +
                                  `(${row.submitted_now} z ${row.max_capacity}).`,
                              )
                            : notifySuccess("Přihláška je vrácena zpět.");
                        },
                        onError: (err) => notifyError(err),
                      })
                    }
                  >
                    Vrátit zpět
                  </Table.Action>
                )}
                {e.permissions.can_delete && (
                  <Table.Action
                    appearance="button"
                    variant="danger"
                    onClick={() =>
                      confirm({
                        title: "Smazat odhlášku",
                        confirmLabel: "Smazat natrvalo",
                        variant: "danger",
                        children: (
                          <>
                            <Text>
                              Přihláška <strong>ČP {e.entry_number}</strong> týmu{" "}
                              <strong>{e.team_name}</strong> zmizí ze soutěže úplně: ze
                              seznamu odhlášek i ze startovní listiny.
                            </Text>
                            <Alert variant="warn">
                              Odhláška je běžně doklad o tom, že se tým přihlásil a zase
                              odhlásil, a nemaže se. Toto je úklid pro chybný záznam;
                              o samotném smazání zůstane zápis v auditním logu.
                            </Alert>
                          </>
                        ),
                        onConfirm: () => remove.mutateAsync(e.id),
                      })
                    }
                  >
                    Smazat
                  </Table.Action>
                )}
              </Table.Actions>
            );
          },
        },
      ]}
    />
  );

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header
          title="Přihlášky"
          subtitle={competition.name}
          actions={
            // While the hall runs the competition a new přihláška waits:
            // its ČP comes from the cloud's counters (A.6b).
            canManuallyAdd &&
            !competition.run_elsewhere && (
              // Step 0 of the shared wizard: pick the team, then compose
              // on its page. No dialog anywhere in the entry flow.
              <Link to={`/soutez/${compId}/nova-prihlaska`}>
                <Button variant="primary">Přidat přihlášku</Button>
              </Link>
            )
          }
        />
        <Page.Content stack gap="lg">
          <FilterPanel>
            <FilterField>
              <Select
                label="Disciplína"
                value={disciplineFilter}
                onChange={(e) => setFilter("discipline", e.target.value)}
              >
                <option value="">Všechny disciplíny</option>
                {(document?.disciplines ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField wide>
              <Select
                label="Tým"
                value={teamFilter}
                onChange={(e) => setFilter("team", e.target.value)}
              >
                <option value="">Všechny týmy</option>
                {teamNames.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            </FilterField>
            {competition.status === "running" && (
              <FilterField>
                <Switch
                  label="Jen z prezence"
                  checked={checkInFilter}
                  onChange={(e) => setFilter("prezence", e.target.checked ? "1" : "")}
                />
              </FilterField>
            )}
          </FilterPanel>

          <Tabs
            aria-label="Přihlášky"
            value={tab}
            onChange={(v) => setTab(v as Tab)}
            tabs={[
              { id: "submitted", label: `Podané přihlášky (${submitted.length})` },
              { id: "withdrawn", label: `Odhlášky (${withdrawn.length})` },
            ]}
          />

          {/* Three states, not two. `groups` is empty while the request is in
              flight, so suppressing only the EmptyState left the whole area
              below the tabs blank — the page looked broken rather than busy. */}
          {entriesLoading ? (
            <LoadingState label="Načítám přihlášky…" />
          ) : visible.length === 0 ? (
            <EmptyState
              title={tab === "submitted" ? "Žádné přihlášky" : "Žádné odhlášky"}
              description={
                tab === "submitted"
                  ? "Zatím se nikdo nepřihlásil, nebo filtrům nic neodpovídá."
                  : "Žádná přihláška nebyla odhlášena."
              }
            />
          ) : (
            [...groups.entries()].map(([categoryId, rows]) => {
              const ref = index?.combos.get(categoryId);
              return (
                <Card key={categoryId}>
                  <Card.Header
                    title={ref ? comboLabel(ref) : "Kombinace"}
                    subtitle={`${rows.length}×`}
                    subtitlePlacement="right"
                  />
                  <Card.Content padding="none">
                    {entryRows(rows)}
                  </Card.Content>
                </Card>
              );
            })
          )}
        </Page.Content>
      </Page>

      {modal.kind === "detail" && (
        <EntryDetailModal
          entry={detailEntry}
          combo={
            detailEntry ? index?.combos.get(detailEntry.discipline_category_id) : undefined
          }
          // The organizer uploads on a club's behalf and has no deadline
          // of their own; the warnings are written for them, so the grace
          // band may be named (see warningMessage).
          canUploadMusic={!isFrozen(competition) && !readOnly}
          audience="organizer"
          onClose={close}
        />
      )}

      <ConfirmDialog
        open={modal.kind === "withdraw"}
        title="Odhlásit přihlášku"
        confirmLabel="Odhlásit"
        variant="danger"
        error={withdraw.error}
        busy={withdraw.isPending}
        onClose={close}
        onConfirm={() => {
          if (modal.kind !== "withdraw") return;
          withdraw.mutate({ id: modal.entry.id, note: withdrawNote.trim() }, { onSuccess: close });
        }}
      >
        {modal.kind === "withdraw" && (
          <>
            <Text>
              Přihláška týmu <strong>{modal.entry.team_name}</strong> zůstane evidovaná
              jako odhláška; záznam se nemaže.
            </Text>
            <Textarea
              label="Důvod (nepovinné)"
              rows={2}
              value={withdrawNote}
              onChange={(e) => setWithdrawNote(e.target.value)}
            />
          </>
        )}
      </ConfirmDialog>

    </>
  );
}

/** What the prezence left with a přihláška: handed over, or still open
 * in the hall. It goes once the problem does. */
function CheckInNote({ checkin }: { checkin: EntryCheckIn }) {
  const who = checkin.missing_names.join(", ");
  const at = checkin.handoff_at_ms ? formatTime(dayjs(checkin.handoff_at_ms)) : "";
  return (
    <div>
      <Badge variant="warn">
        {checkin.handed_off ? `Předáno z prezence ${at}` : CHECKIN_PROBLEM_LABELS[checkin.problem]}
      </Badge>
      {who && <Text variant="muted"> Chybí: {who}</Text>}
      {checkin.handoff_note && <Text variant="muted"> · {checkin.handoff_note}</Text>}
    </div>
  );
}

